import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useTrackAgentThreadUnarchived } from '@air/analytics';
import { CONFLICT } from '@air/errors';
import { isAirrorInstance } from '@air/utils-error';

import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { usePrivateWorkspaceAgentSessionUnarchiveMutation } from '~/components/v2/components/workspace/workspace-agent-panel/queries/mutations/usePrivateWorkspaceAgentSessionUnarchiveMutation';
import { getPrivateWorkspaceAgentSessionGetKey } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionGet';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

export const useUnarchiveWorkspaceAgentTask = () => {
  const queryClient = useQueryClient();
  const { getActiveSessionId, refreshSessions, workspaceId } = usePrivateWorkspaceAgentPanelSession();
  const { trackAgentThreadUnarchived } = useTrackAgentThreadUnarchived();
  const { isPending, mutateAsync, variables } = usePrivateWorkspaceAgentSessionUnarchiveMutation();

  const unarchiveTask = useCallback(
    async (sessionId: string) => {
      try {
        await mutateAsync({
          agentType: 'workspace',
          sessionId,
          workspaceId,
        });

        trackAgentThreadUnarchived();

        const { focusComposer, panelView } = useWorkspaceAgentPanelStore.getState();

        if (getActiveSessionId() === sessionId && panelView === 'chat') {
          focusComposer();
        }

        await refreshSessions();
      } catch (error) {
        if (isAirrorInstance(error) && error.type === CONFLICT.type) {
          await refreshSessions();

          if (getActiveSessionId() === sessionId) {
            void queryClient.invalidateQueries({
              queryKey: getPrivateWorkspaceAgentSessionGetKey({ sessionId, workspaceId }),
            });
          }

          return;
        }

        throw error;
      }
    },
    [getActiveSessionId, mutateAsync, queryClient, refreshSessions, trackAgentThreadUnarchived, workspaceId],
  );

  const isUnarchivingSession = useCallback(
    (sessionId: string) => isPending && variables?.sessionId === sessionId,
    [isPending, variables?.sessionId],
  );

  return {
    isUnarchivingSession,
    unarchiveTask,
  };
};
